import React, { useState, useRef, useMemo } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import './AirportMarker.css';
import { useAirportSettings } from '../contexts/SettingsContext';

const AirportMarker = ({ airport }) => {
  const position = [airport.latitude, airport.longitude];
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const markerRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  // Get airport colour from settings
  const airportSettings = useAirportSettings();
  const airportColour = airportSettings?.colour || '#FFFFFF';

  // Convert hex colour to CSS filter for SVG
  const colourFilter = useMemo(() => {
    // For white, no filter needed
    if (airportColour.toUpperCase() === '#FFFFFF') {
      return 'none';
    }
    // Convert hex to RGB
    const hex = airportColour.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Convert to HSL for filter calculation
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    const l = (max + min) / 2;

    let h = 0;
    let s = 0;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
        case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
        case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
        default: break;
      }
    }

    const hueDeg = Math.round(h * 360);
    const satPercent = Math.round(s * 100);
    const lightPercent = Math.round(l * 100);
    const invertPercent = Math.round(lightPercent * 0.8 + 20);
    const sepiaPercent = Math.min(100, satPercent + 50);
    const saturatePercent = Math.round(500 + satPercent * 10);

    return `brightness(0) saturate(100%) invert(${invertPercent}%) sepia(${sepiaPercent}%) saturate(${saturatePercent}%) hue-rotate(${hueDeg}deg) brightness(${(0.9 + l * 0.2).toFixed(2)})`;
  }, [airportColour]);

  // Determine icon size and opacity based on airport type
  const getIconProperties = (type) => {
    switch (type) {
      case 'large_airport':
        return { size: 20, opacity: 0.35, fontSize: '10px', fontWeight: '600' };
      case 'medium_airport':
        return { size: 14, opacity: 0.3, fontSize: '9px', fontWeight: '500' };
      case 'small_airport':
        return { size: 10, opacity: 0.25, fontSize: '8px', fontWeight: '500' };
      case 'heliport':
        return { size: 8, opacity: 0.2, fontSize: '7px', fontWeight: '500' };
      default:
        return { size: 10, opacity: 0.25, fontSize: '8px', fontWeight: '500' };
    }
  };

  // Create custom icon with airport_white.svg and IATA code label
  // Memoized to prevent unnecessary re-renders that cause flashing
  const airportIcon = useMemo(() => {
    const props = getIconProperties(airport.type);
    const filterStyle = colourFilter !== 'none' ? `filter: ${colourFilter};` : '';
    const iconHtml = `
      <div style="position: relative; display: flex; flex-direction: column; align-items: center; gap: 0px; opacity: 0.5;">
        <img
          src="/icons/airport_white.svg"
          style="
            width: ${props.size}px;
            height: ${props.size}px;
            ${filterStyle}
          "
          alt="${airport.iata || airport.icao}"
        />
        <div style="
          font-size: ${props.fontSize};
          font-weight: bold;
          font-family: 'Courier New', 'Consolas', monospace;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: ${airportColour};
          background: transparent;
          padding: 1px 3px;
          border-radius: 2px;
          white-space: nowrap;
        ">
          ${airport.iata || airport.icao?.substring(1) || ''}
        </div>
      </div>
    `;

    return L.divIcon({
      html: iconHtml,
      className: 'airport-marker',
      iconSize: [props.size + 10, props.size + 20], // Extra space for label below
      iconAnchor: [(props.size + 10) / 2, props.size / 2],
    });
  }, [airport.type, airport.iata, airport.icao, airportColour, colourFilter]);

  const handleMouseEnter = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    // Add a small delay before closing to prevent flickering
    closeTimeoutRef.current = setTimeout(() => {
      setIsTooltipOpen(false);
    }, 100);
  };

  return (
    <Marker
      ref={markerRef}
      position={position}
      icon={airportIcon}
      eventHandlers={{
        mouseover: handleMouseEnter,
        mouseout: handleMouseLeave
      }}
    >
      {isTooltipOpen && (
        <Tooltip
          direction="top"
          offset={[0, -10]}
          opacity={1}
          permanent={true}
          interactive={false}
          className="airport-tooltip"
        >
          <div className="airport-tooltip-content">
            <div className="airport-tooltip-header">
              <img
                src="/icons/airport_white.svg"
                alt="Airport"
                className="airport-tooltip-icon"
              />
              <div className="airport-code-large">
                {airport.iata || airport.icao}
              </div>
              {airport.iata && airport.icao && (
                <div className="airport-icao-header">
                  {airport.icao}
                </div>
              )}
            </div>
            <div className="airport-tooltip-details">
              <div className="airport-name">{airport.name}</div>
              <div className="airport-location">
                {airport.city || airport.municipality}
              </div>
            </div>
          </div>
        </Tooltip>
      )}
    </Marker>
  );
};

export default AirportMarker;